'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { legalApi } from '@/lib/api';

export type LegalFooterLinkItem = {
  label: string;
  href: string;
  slug?: string | null;
};

const FALLBACK_LEGAL_LINKS: LegalFooterLinkItem[] = [
  { label: 'Terms & Conditions', href: '/legal/terms-and-conditions', slug: 'terms-and-conditions' },
  { label: 'Privacy Policy', href: '/legal/privacy-policy', slug: 'privacy-policy' },
];

const FOOTER_CACHE_MS = 10 * 60 * 1000;
let cachedFooterLinks: LegalFooterLinkItem[] | null = null;
let footerCacheTime = 0;
let footerRequest: Promise<LegalFooterLinkItem[]> | null = null;

function loadLegalFooterLinks(): Promise<LegalFooterLinkItem[]> {
  if (cachedFooterLinks && Date.now() - footerCacheTime < FOOTER_CACHE_MS) {
    return Promise.resolve(cachedFooterLinks);
  }
  if (footerRequest) return footerRequest;

  footerRequest = legalApi
    .footerLinks()
    .then((res) => {
      const data = res.data?.data;
      const links = Array.isArray(data) && data.length > 0 ? data : FALLBACK_LEGAL_LINKS;
      cachedFooterLinks = links;
      footerCacheTime = Date.now();
      return links;
    })
    .catch(() => FALLBACK_LEGAL_LINKS)
    .finally(() => {
      footerRequest = null;
    });

  return footerRequest;
}

export function useLegalFooterLinks() {
  const [links, setLinks] = useState<LegalFooterLinkItem[]>(
    cachedFooterLinks ?? FALLBACK_LEGAL_LINKS
  );

  useEffect(() => {
    let active = true;
    void loadLegalFooterLinks().then((next) => {
      if (active) setLinks(next);
    });
    return () => {
      active = false;
    };
  }, []);

  return links;
}

export function LegalFooterLinkList({
  className = 'space-y-1.5 text-nature-muted text-sm',
  linkClassName = 'hover:text-nature-text transition-colors',
}: {
  className?: string;
  linkClassName?: string;
}) {
  const links = useLegalFooterLinks();

  return (
    <ul className={className}>
      {links.map((item) => (
        <li key={`${item.href}-${item.label}`}>
          <Link href={item.href} className={linkClassName}>
            {item.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}
